import { NavLink, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "./TableItem.module.css";

import { selectAuthUserId, selectLoginToken } from "../../features/auth/AuthSlice";
import { useSaveSubjectPlan } from "../../features/subjectPlan/hooks/useSaveSubjectPlan.js";
import { useDeleteSubjectPlan } from "../../features/subjectPlan/hooks/useDeleteSubjectPlan.js";
import { calculateCompletionPercentage } from "../../common/functions";
import labels from "../../common/labels.json";

import {
  IconDuplicate,
  IconEye,
  IconShare,
  IconTrash,
} from "../../common/Icons/Icons";

const TableItem = ({
  data,
  showShareModal,
  freezeClassName,
  displaySharedOnly,
}) => {
  const history = useHistory();
  const { t } = useTranslation("subjectPlan");
  const currentUserId = useSelector(selectAuthUserId);
  const jwt = useSelector(selectLoginToken);

  const planCompletionPercentage = calculateCompletionPercentage(data);

  // Simple JWT decoder function
  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      return JSON.parse(jsonPayload);
    } catch (err) {
      return null;
    }
  };

  // Get user's group roles from JWT
  const getUserGroupRoles = () => {
    try {
      if (!jwt) return [];
      const decoded = decodeJWT(jwt);
      return decoded?.groupRoles || [];
    } catch (err) {
      return [];
    }
  };

  // Check if user has write/admin access through groups
  const hasGroupWriteAccess = () => {
    const userGroupRoles = getUserGroupRoles();
    const groupAccessRecords = data.access?.filter(a => a.groupId && (a.access === 'W' || a.access === 'A')) || [];

    for (const groupAccess of groupAccessRecords) {
      const userRole = userGroupRoles.find(gr => gr.groupId === groupAccess.groupId);
      // User has write access if they're admin or write in a group with 'W' or 'A' access
      if (userRole && ['admin', 'write'].includes(userRole.roleName)) {
        return true;
      }
    }
    return false;
  };

  // Determine how the user has access to this plan
  const getAccessVia = () => {
    if (!data.access || data.access.length === 0) return null;

    // Check for direct user access
    const directAccess = data.access.find(
      acc => acc.userId === currentUserId && !acc.groupId
    );
    if (directAccess) return t('common:direct');

    // Check for group access
    const groupAccess = data.access.find(acc => acc.groupId && acc.group);
    if (groupAccess) return groupAccess.group.name;

    return null;
  };

  // Check if user has direct Admin access to the plan
  const hasDirectAdminAccess = data.access?.some(
    access => access.userId === currentUserId && access.access === 'A'
  );

  // Check if user has direct Write access
  const hasDirectWriteAccess = data.access?.some(
    access => access.userId === currentUserId && (access.access === 'W' || access.access === 'A')
  );

  // User can share if they own the plan OR have Admin/Write access (direct or through groups)
  // Check backend's userAccess field first, then fallback to JWT group roles check
  const canShare = data.userId === currentUserId ||
                   hasDirectAdminAccess ||
                   hasDirectWriteAccess ||
                   (data.userAccess && ['A', 'W'].includes(data.userAccess)) ||
                   hasGroupWriteAccess();

  const { mutate: savePlan } = useSaveSubjectPlan();
  const { mutate: deletePlan } = useDeleteSubjectPlan();
  const userId = useSelector(selectAuthUserId);

  const allExistingLabels = labels.map((group) => group.items).flat();

  function goToEditPlan(id) {
    history.push(`/temaplan/ny/${id}`);
  }

  function handleDeleteItem() {
    const decision = window.confirm(
      t("common:confirmations.removeSubjectPlan")
    );

    if (decision) deletePlan(data.id);
  }

  function handleDisplayItem() {
    history.push(`/temaplan/visning/${data.id}`);
    window.scrollTo({
      top: 0,
    });
  }

  function handleShareButtonClick() {
    showShareModal(data.id);
  }

  function handleDuplicatePlan() {
    const post = {
      title: `${data.title || t("common:unknown")} - kopi`,
      grade: data.grade || "",
      subject: data.subject || "",
      qualityMarkers: data.qualityMarkers || "",
      time: +data.time || 0,
      labels:
        // Filter out labels that no longer exist in the system
        data.labels
          ?.split(",")
          .filter((label) => allExistingLabels.includes(label))
          ?.join(",") || "",
      involvement: data.involvement || "",
      goals: data.goals || "",
      feedback: data.feedback || "",
      curriculumBase: data.curriculumBase || "",
      concepts: data.concepts || "",
      challenges: data.challenges || "",
      assessment: data.assessment || "",
      summary: data.summary || "",
      reason: data.reason || "",
      type: data.type || "",
      resources: data.resources || "",
      instructions: data.instructions || "",
      activities:
        data.activities?.map((activity, index) => ({
          type: activity.type,
          resources: activity.resources,
          instructions: activity.instructions,
          homework: activity.homework,
          title: activity.title,
          position: activity.position || index,
        })) || [],
    };

    savePlan(post, {
      onSuccess: (response) => {
        if (response?.data?.id) goToEditPlan(response.data?.id);
      },
    });
  }

  function hasWriteAccessTo(planData) {
    if (userId === planData.userId) return true;

    // Use computed userAccess field from backend (includes inherited admin roles)
    if (planData.userAccess === 'W') return true;

    // Fallback to direct user access check
    const myAccess = planData.access?.find(
      (access) => access.userId === userId
    );

    return myAccess?.access === "W" ? true : false;
  }

  return (
    <tr className={styles.row}>
      <td className={freezeClassName}>
        <NavLink
          to={
            hasWriteAccessTo(data)
              ? `/temaplan/rediger/${data.id}`
              : `/temaplan/visning/${data.id}`
          }
        >
          <span
            role="img"
            aria-label="fylt ut prosent"
            className={styles.percentageIcon}
            style={{
              "--percentage": planCompletionPercentage,
            }}
            title={`${Math.round(planCompletionPercentage * 100)}% fylt ut`}
          />

          {data.title || " "}
        </NavLink>
      </td>

      <td className={styles.freezeSubject}>
        {data.subject.split(";").map((item, key) => (
          <span key={key}>
            {item}
            <br />
          </span>
        )) || " "}
      </td>

      <td>
        {data.grade
          ? data.grade.split(";").map((item, index) => (
              <span key={`grade-${index}`}>
                {item}. trinn <br />
              </span>
            ))
          : null}
      </td>

      <td className={styles.labels}>
        {data.labels
          ? data.labels
              .split(",")
              ?.filter((label) => allExistingLabels.includes(label))
              ?.map((label) => t(`subjectPlan:labelTags.tags.${label}`))
              .join(", ")
          : null}
      </td>

      <td>{new Date(data.updatedAt).toLocaleDateString("en-GB")}</td>

      {displaySharedOnly && <td>{getAccessVia()}</td>}

      <td className={styles.icons}>
        <button title="Åpne" onClick={handleDisplayItem}>
          <IconEye />
        </button>

        {canShare && (
          <button title="Del" onClick={handleShareButtonClick}>
            <IconShare />
          </button>
        )}

        <button title="Dupliser" onClick={handleDuplicatePlan}>
          <IconDuplicate />
        </button>

        {displaySharedOnly ? null : (
          <button title="Slett" onClick={handleDeleteItem}>
            <IconTrash />
          </button>
        )}
      </td>
    </tr>
  );
};

export default TableItem;
